"use client";

// import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Copy, Trash, CheckCheck, CircleCheck, X } from "lucide-react";
import { useAction } from "@/hooks/use-actions";
import { copyCard } from "@/actions/main-board-actions/copy-card";
import { deleteCard } from "@/actions/main-board-actions/delete-card";
import { useParams } from "next/navigation";
import { toast } from "sonner";
import { useCardModal } from "@/hooks/use-card-model";
import { CardWithSubTodos } from "@/types/pm-ai-types/pmAi-types";
import { Doc } from "@/convex/_generated/dataModel";
import { Button } from "@heroui/react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import React from "react";
interface ActionsProps {
  data: Doc<"todos">;
}
export const Actions = ({
  data
}: ActionsProps) => {
  // console.log("[actions] data: ", data);

  const params = useParams();
  const cardModal = useCardModal();
  const {
    execute: executeCopyCard,
    isLoading: isLoadingCopy
  } = useAction(copyCard, {
    onSuccess: data => {
      toast.success(`Card "${data.title}" copied`);
      cardModal.onClose();
    },
    onError: error => {
      toast.error(error);
    }
  });
  const {
    execute: executeDeleteCard,
    isLoading: isLoadingDelete
  } = useAction(deleteCard, {
    onSuccess: data => {
      toast.success(`Card "${data.title}" deleted`);
      cardModal.onClose();
    },
    onError: error => {
      toast.error(error);
    }
  });
  const checkATodo = useMutation(api.pmAi.todos.checkATodo);
  const unCheckATodo = useMutation(api.pmAi.todos.unCheckATodo);
  const [isMarking, setIsMarking] = React.useState(false);
  const [isCompleted, setIsCompleted] = React.useState(data.isCompleted);
  React.useEffect(() => {
    setIsCompleted(data.isCompleted);
  }, [data.isCompleted]);
  const onMarkAsDone = async () => {
    setIsMarking(true);
    try {
      if (isCompleted) {
        const result = await unCheckATodo({
          taskId: data._id
        });
        if (result?.error) {
          toast.error(result.error.message || "Failed to uncheck task");
          return;
        }
        setIsCompleted(false);
        toast.success("Marked as not done", {
          duration: 3000
        });
      } else {
        const result = await checkATodo({
          taskId: data._id
        });
        if (result?.error) {
          toast.error(result.error.message || "Failed to complete task");
          return;
        }
        setIsCompleted(true);
        toast.success(<>
            <CheckCheck className="mr-2 h-4 w-4 text-green-700 dark:text-green-500" />
            Marked as done
          </>, {
          duration: 3000
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[Actions.onMarkAsDone] - error:", error);
      toast.error("An error occurred while updating the task");
    } finally {
      setIsMarking(false);
    }
  };
  const onCopy = () => {
    // cardModal.onClose();

    // TODO L Change this for multi project boards
    const projectId = data.projectId;
    executeCopyCard({
      id: data._id,
      boardId: projectId
    });
  };
  const onDelete = () => {
    // TODO L Change this for multi project boards
    const boardId = data.projectId;
    executeDeleteCard({
      id: data._id,
      boardId
    });
  };
  return <div className="mt-2 space-y-2" data-sentry-component="Actions" data-sentry-source-file="actions.tsx">
      <p className="text-sm font-semibold">Actions</p>
      <Button aria-label={isCompleted ? "mark as not done" : "mark as done"} onClick={onMarkAsDone} isDisabled={isLoadingCopy || isMarking} isLoading={isLoadingCopy || isMarking} startContent={isLoadingCopy || isMarking ? <></> : isCompleted ? <X className="mr-2 h-4 w-4" /> : <CheckCheck className="mr-2 h-4 w-4 text-green-700 dark:text-green-500" />} variant={"solid"} className="w-full justify-start" size={"sm"} color={isCompleted ? "default" : "secondary"} data-sentry-element="Button" data-sentry-source-file="actions.tsx">
        {isCompleted ? "Mark as not done" : "Mark as done"}
      </Button>
      <Button aria-label="copy" onClick={onCopy} isDisabled={isLoadingCopy} isLoading={isLoadingCopy} startContent={isLoadingCopy ? <></> : <Copy className="mr-2 h-4 w-4" />} variant={"solid"} className="w-full justify-start" size={"sm"} color="secondary" data-sentry-element="Button" data-sentry-source-file="actions.tsx">
        Copy
      </Button>
      <Button aria-label="delete" onClick={onDelete} isDisabled={isLoadingDelete} isLoading={isLoadingDelete} startContent={isLoadingDelete ? <></> : <Trash className="mr-2 h-4 w-4" />} variant={"solid"} className="w-full justify-start" size={"sm"} color="danger" data-sentry-element="Button" data-sentry-source-file="actions.tsx">
        Delete
      </Button>
    </div>;
};
Actions.Skeleton = function ActionsSkeleton() {
  return <div className="mt-2 space-y-2">
      <Skeleton className="h-4 w-20 bg-neutral-200 dark:bg-neutral-700 " />
      <Skeleton className="h-8 w-full bg-neutral-200 dark:bg-neutral-700 " />
      <Skeleton className="h-8 w-full bg-neutral-200 dark:bg-neutral-700 " />
    </div>;
};